import React, { useEffect, useMemo, useState } from "react";
import {
  localMenuImageCandidates,
  resolveMenuImageUrl,
} from "../../utils/menuImageUrl";

export const FOOD_PLACEHOLDER = "/placeholder-food.svg";
export const LOGO_PLACEHOLDER = "/ruchira-logo.png";

type SafeImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src" | "onError"
> & {
  src?: string | null;
  fallbackSrc?: string;
  /** When true, hide the broken icon entirely and show a soft placeholder block */
  hideBrokenIcon?: boolean;
};

function isUsableSrc(value?: string | null): value is string {
  if (!value) return false;
  const s = String(value).trim();
  if (!s || s === "undefined" || s === "null") return false;
  // Allow site-relative paths and absolute http(s) URLs only
  if (s.startsWith("/") || s.startsWith("data:") || s.startsWith("blob:")) return true;
  if (/^https?:\/\//i.test(s)) return true;
  return false;
}

/**
 * Image that never shows the browser's broken-image icon.
 * Falls back to Hosting copies of menu photos (when Storage is unavailable),
 * then to an intentional placeholder.
 */
export const SafeImage: React.FC<SafeImageProps> = ({
  src,
  alt = "",
  fallbackSrc = FOOD_PLACEHOLDER,
  className,
  hideBrokenIcon = true,
  ...rest
}) => {
  const queue = useMemo(() => {
    const resolved = resolveMenuImageUrl(src);
    const primary = isUsableSrc(resolved)
      ? String(resolved).trim()
      : isUsableSrc(src)
        ? String(src).trim()
        : null;

    const extras = localMenuImageCandidates(src).filter(
      (c) => c !== primary && c !== fallbackSrc
    );

    const list: string[] = [];
    if (primary) list.push(primary);
    for (const c of extras) {
      if (!list.includes(c)) list.push(c);
    }
    if (!list.includes(fallbackSrc)) list.push(fallbackSrc);
    return list;
  }, [src, fallbackSrc]);

  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setIndex(0);
    setFailed(false);
  }, [src, fallbackSrc]);

  const currentSrc = queue[Math.min(index, queue.length - 1)] || fallbackSrc;

  if (failed && hideBrokenIcon) {
    return (
      <div
        className={`flex items-center justify-center bg-orange-50 text-orange-400 ${className || ""}`}
        role="img"
        aria-label={alt || "Image unavailable"}
      >
        <span className="text-2xl leading-none select-none" aria-hidden>
          🍽️
        </span>
      </div>
    );
  }

  return (
    <img
      {...rest}
      src={currentSrc}
      alt={failed ? "" : alt}
      className={className}
      loading={rest.loading || "lazy"}
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => {
        if (index + 1 < queue.length) {
          setIndex((i) => i + 1);
        } else {
          setFailed(true);
        }
      }}
    />
  );
};

export default SafeImage;
